/**
 * Default AI Calling system prompt + first-message templates for Admin Settings.
 * Job-specific values use {{variable}} placeholders; job screen fills them later.
 * Official infield/outcome/voice lists are fallbacks — live values come from
 * ATS admin config via ai-calling-catalog.
 */

import {
  getCreateInfieldsForCallType,
  getCreateOutcomesForCallType,
  getInitiateInfieldKeys,
  getDriveInitiateInfieldKeys,
  getLanguageOptions,
  getVoiceCatalog,
  getSchemaSlotLimit,
  getPresetPromptLinesForType,
} from '@/utils/ai-calling-catalog'

export const DEFAULT_COMPANY_NAME = 'Sattva Human Private Limited'

export const DEFAULT_AGENT_NAME = 'Nikita'

/** Spoken fallbacks when a job/candidate field is missing — never leave {{tokens}} for TTS. */
export const SPOKEN_FALLBACKS = {
  agent_name: DEFAULT_AGENT_NAME,
  company: DEFAULT_COMPANY_NAME,
  client: 'the client',
  role: 'this role',
  job_title: 'this role',
  industry: 'this industry',
  location: 'the job location',
  job_type: 'full time',
  work_summary: 'the work in this role',
  preferred_qualification: 'the required qualification',
  min_experience_years: 'the required',
  requirements: 'the job requirements',
  mandatory_skill: 'the required skill',
  equipment_examples: 'the tools used on this job',
  candidate_name: 'Rahul Sharma',
  drive_name: 'the hiring drive',
  drive_dates: 'the upcoming dates',
  drive_location: 'the drive location',
}

/** Generic samples for template preview in Admin Settings (not job-specific). */
export const AI_CALLING_VARIABLES = [
  { key: 'agent_name', sample: SPOKEN_FALLBACKS.agent_name },
  { key: 'company', sample: SPOKEN_FALLBACKS.company },
  { key: 'client', sample: SPOKEN_FALLBACKS.client },
  { key: 'role', sample: SPOKEN_FALLBACKS.role },
  { key: 'job_title', sample: SPOKEN_FALLBACKS.job_title },
  { key: 'industry', sample: SPOKEN_FALLBACKS.industry },
  { key: 'location', sample: SPOKEN_FALLBACKS.location },
  { key: 'job_type', sample: SPOKEN_FALLBACKS.job_type },
  { key: 'work_summary', sample: SPOKEN_FALLBACKS.work_summary },
  { key: 'preferred_qualification', sample: SPOKEN_FALLBACKS.preferred_qualification },
  { key: 'min_experience_years', sample: SPOKEN_FALLBACKS.min_experience_years },
  { key: 'requirements', sample: SPOKEN_FALLBACKS.requirements },
  { key: 'mandatory_skill', sample: SPOKEN_FALLBACKS.mandatory_skill },
  { key: 'equipment_examples', sample: SPOKEN_FALLBACKS.equipment_examples },
  { key: 'candidate_name', sample: SPOKEN_FALLBACKS.candidate_name },
  { key: 'drive_name', sample: SPOKEN_FALLBACKS.drive_name },
  { key: 'drive_dates', sample: SPOKEN_FALLBACKS.drive_dates },
  { key: 'drive_location', sample: SPOKEN_FALLBACKS.drive_location },
]

/** Call type constants for AI Calling wizard. */
export const AI_CALL_TYPES = {
  FRESH: 'fresh',
  DRIVE: 'drive',
  OTHER: 'other',
}

/** Legacy alias — map follow_up to drive when loading old data. */
export function normalizeCallType(callType) {
  const t = (callType || AI_CALL_TYPES.FRESH).toString().trim()
  if (t === 'follow_up') return AI_CALL_TYPES.DRIVE
  if (t === AI_CALL_TYPES.DRIVE) return AI_CALL_TYPES.DRIVE
  if (t === AI_CALL_TYPES.OTHER) return AI_CALL_TYPES.OTHER
  return AI_CALL_TYPES.FRESH
}

/** One-line prompt presets for Screening / Drive (Phase 1 script selection). */
export const PRESET_PROMPT_LINES = {
  [AI_CALL_TYPES.FRESH]: [
    {
      id: 'default',
      label: 'Standard screening',
      brief: '',
      description: 'Interest, location, qualification & experience',
    },
    {
      id: 'quick_interest',
      label: 'Quick interest check',
      brief: 'Quick 2-minute interest check for the role',
      description: 'Short call — confirm availability only',
    },
    {
      id: 'exp_focus',
      label: 'Experience focused',
      brief: 'Screen carefully for minimum experience and industry background',
      description: 'Emphasize years of experience & industry match',
    },
    {
      id: 'qual_focus',
      label: 'Qualification focused',
      brief: 'Verify highest qualification and mandatory skills match',
      description: 'Emphasize education & skill requirements',
    },
  ],
  [AI_CALL_TYPES.DRIVE]: [
    {
      id: 'default',
      label: 'Standard drive invite',
      brief: '',
      description: 'Invite to walk-in drive with dates & venue',
    },
    {
      id: 'walkin',
      label: 'Walk-in reminder',
      brief: 'Remind candidate about upcoming walk-in drive and confirm attendance',
      description: 'Drive dates, venue & attendance confirmation',
    },
    {
      id: 'registered',
      label: 'Registered candidate follow-up',
      brief: 'Follow up with registered candidates about drive participation',
      description: 'For candidates already in the drive funnel',
    },
  ],
}

/** Preset list for a call type (empty for Other). Admin config can replace these. */
export function getPresetPromptLines(callType) {
  const ct = normalizeCallType(callType)
  return getPresetPromptLinesForType(ct, PRESET_PROMPT_LINES[ct] || [])
}

/** Recruiter-facing label for call type. */
export function getCallTypeLabel(callType) {
  const t = normalizeCallType(callType)
  if (t === AI_CALL_TYPES.DRIVE) return 'Drive Followup'
  if (t === AI_CALL_TYPES.OTHER) return 'Other'
  return 'Screening'
}

/** Format drive_dates from job — string, JSON array, or array of date strings. */
export function formatDriveDates(raw) {
  if (raw == null || raw === '') return ''
  if (Array.isArray(raw)) {
    return raw
      .map(d => {
        if (d == null) return ''
        if (typeof d === 'object') {
          return (d.date || d.title || d.label || '').toString().trim()
        }
        return String(d).trim()
      })
      .filter(Boolean)
      .join(', ')
  }
  const str = String(raw).trim()
  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str)
      if (Array.isArray(parsed)) return formatDriveDates(parsed)
    } catch (e) {
      /* use raw string */
    }
  }
  return str
}

/** Whether job has enough drive metadata for drive-call mode. */
export function jobHasDriveContext(job = {}) {
  const name = (job.drive_name || job.driveName || '').toString().trim()
  const dates = formatDriveDates(job.drive_dates ?? job.driveDates)
  return Boolean(name || dates)
}

/** Org-level wizard defaults — job fields stay as {{tokens}} until call time. */
export function defaultOrgPromptFields(overrides = {}) {
  return {
    agent_name: DEFAULT_AGENT_NAME,
    company: DEFAULT_COMPANY_NAME,
    ...overrides,
  }
}

/** Normalize a titled field — string, array, or { title / name } objects. */
export function formatNamedField(value) {
  if (value == null || value === '') return ''
  if (Array.isArray(value)) {
    const items = value
      .map(item => formatNamedField(item))
      .filter(Boolean)
    return items.join(', ')
  }
  if (typeof value === 'object') {
    return (
      value.title
      || value.name
      || value.label
      || value.city
      || value.location
      || ''
    ).toString().trim()
  }
  const text = String(value).trim()
  if (!text || text === '[object Object]') return ''
  return text
}

/** Normalize job location — string, array of strings, or { title } objects. */
export function formatJobLocation(location) {
  const formatted = formatNamedField(location)
  if (!formatted) return ''
  return formatted.split(',')[0].trim() || formatted
}

function parseSkillList(raw) {
  const text = formatNamedField(raw)
  if (!text) return []
  return text.split(/[,;|/]+/).map(part => part.trim()).filter(Boolean)
}

export function looksLikeAgentCode(value) {
  const s = (value || '').toString().trim()
  if (!s) return false
  return /^AGT[_-]/i.test(s) || /^[A-Z]{2,5}_[A-F0-9]{6,}$/i.test(s)
}

/** Prefer a speakable agent name over an Indus agent id. */
export function humanAgentName(...candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const name = formatNamedField(candidates[i])
    if (name && !looksLikeAgentCode(name)) return name
  }
  return DEFAULT_AGENT_NAME
}

/** Industry / vertical for prompts — vertical → domain → business → category. */
export function resolveJobIndustry(job = {}) {
  const vertical = formatNamedField(job.vertical)
  if (vertical) return vertical
  const domain = formatNamedField(job.domain)
  if (domain) return domain
  const businessStr = formatNamedField(job.business)
  if (businessStr) return businessStr
  return formatNamedField(job.category)
}

/**
 * Map job record → prompt variable values for applyPromptVariables.
 * @param {Object} job — jobData or prop bag with position_name, client, locations, etc.
 * @param {Object} overrides — e.g. agent_name, company from admin config
 */
export function buildJobPromptValues(job = {}, overrides = {}) {
  const title = formatNamedField(
    job.position_name || job.jobTitle || job.job_title || '',
  )
  const role = formatNamedField(job.role) || title
  const client = formatNamedField(job.client || job.clientName)
  const location = formatJobLocation(job.locations ?? job.location)
  const industry = resolveJobIndustry({
    ...job,
    vertical: formatNamedField(job.vertical),
    domain: formatNamedField(job.domain),
    category: formatNamedField(job.category),
    business: job.business,
  })
  const minExp = job.min_exp ?? job.minExp
  const maxExp = job.max_exp ?? job.maxExp
  const minExpStr = formatJobNumericField(minExp)
  const maxExpStr = formatJobNumericField(maxExp)
  const minCtcStr = formatJobNumericField(
    job.min_ctc ?? job.minCtc ?? job.minCTC ?? job.min_salary,
  )
  const maxCtcStr = formatJobNumericField(
    job.max_ctc ?? job.maxCtc ?? job.maxCTC ?? job.max_salary,
  )
  const jobType = formatNamedField(job.type || job.job_type)
  const skillSource = job.mandatory_skills || job.mandatory_skill
    || job.desired_skills || job.desiredSkills || ''
  const skills = parseSkillList(skillSource)
  const desiredSkills = formatNamedField(job.desired_skills || job.desiredSkills)
  const qualification = formatNamedField(
    job.qualification || job.preferred_qualification,
  )

  const company = formatNamedField(
    overrides.company || job.company || job.companyName,
  ) || DEFAULT_COMPANY_NAME
  const agentName = humanAgentName(
    overrides.agent_name,
    job.agent_name,
    job.agentName,
  )

  const values = {}

  values.agent_name = agentName
  values.company = company
  values.company_name = company
  if (client) {
    values.client = client
    values.client_name = client
  }
  if (role) values.role = role
  if (title) values.job_title = title
  if (title) values.position_name = title
  else if (role) values.position_name = role
  if (location) values.location = location
  if (industry) values.industry = industry
  if (minExpStr) {
    values.min_experience_years = minExpStr
    values.min_exp = minExpStr
  }
  if (maxExpStr) {
    values.max_experience_years = maxExpStr
    values.max_exp = maxExpStr
  }
  const expRange = formatExperienceRange(minExpStr, maxExpStr)
  if (expRange) values.experience = expRange
  if (minCtcStr) values.min_ctc = minCtcStr
  if (maxCtcStr) values.max_ctc = maxCtcStr
  const ctcRange = formatCtcRange(minCtcStr, maxCtcStr)
  if (ctcRange) values.ctc = ctcRange
  if (qualification) {
    values.preferred_qualification = qualification
    values.qualification = qualification
  } else if (desiredSkills) {
    values.preferred_qualification = desiredSkills
    values.qualification = desiredSkills
  }
  if (jobType) values.job_type = jobType
  if (desiredSkills) values.work_summary = desiredSkills
  if (skills[0]) values.mandatory_skill = skills[0]
  if (skills.length > 1) {
    values.equipment_examples = skills.slice(1).join(', ')
  } else if (skills[0]) {
    values.equipment_examples = skills[0]
  }

  const reqParts = []
  if (minExpStr) reqParts.push(`Minimum ${minExpStr} years relevant experience`)
  if (industry) reqParts.push(`${industry} experience`)
  if (location) reqParts.push(`willingness to work in ${location}`)
  if (reqParts.length) values.requirements = `${reqParts.join(', ')}.`

  const driveName = formatNamedField(job.drive_name || job.driveName)
  const driveDates = formatDriveDates(job.drive_dates ?? job.driveDates)
  if (driveName) values.drive_name = driveName
  if (driveDates) values.drive_dates = driveDates

  Object.keys(overrides || {}).forEach(k => {
    const v = formatNamedField(overrides[k])
    if (v && !(k === 'agent_name' && looksLikeAgentCode(v))) values[k] = v
  })

  if (values.client && !values.client_name) values.client_name = values.client
  if (values.client_name && !values.client) values.client = values.client_name
  if (values.company && !values.company_name) values.company_name = values.company
  if (values.min_experience_years && !values.min_exp) values.min_exp = values.min_experience_years
  if (values.max_experience_years && !values.max_exp) values.max_exp = values.max_experience_years
  if (!values.experience) {
    values.experience = formatExperienceRange(
      values.min_experience_years || values.min_exp,
      values.max_experience_years || values.max_exp,
    )
  }
  if ((values.min_ctc || values.max_ctc) && !values.ctc) {
    values.ctc = formatCtcRange(values.min_ctc, values.max_ctc)
  }
  if (values.preferred_qualification && !values.qualification) {
    values.qualification = values.preferred_qualification
  }
  if (values.location && !values.drive_location) {
    values.drive_location = values.location
  }

  return values
}

function formatJobNumericField(value) {
  if (value == null || value === '') return ''
  const text = String(value).trim()
  if (!text || text === 'null' || text === 'undefined') return ''
  return text
}

function formatExperienceRange(minStr, maxStr) {
  if (minStr && maxStr) return `${minStr}-${maxStr}`
  return minStr || maxStr || ''
}

function formatCtcRange(minStr, maxStr) {
  if (minStr && maxStr) return `${minStr}-${maxStr} Lacs`
  if (minStr) return `${minStr} Lacs`
  if (maxStr) return `up to ${maxStr} Lacs`
  return ''
}

export const DEFAULT_SYSTEM_PROMPT_HINGLISH = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. You are screening candidates for the {{role}} role in the {{industry}} for {{client}}, based in {{location}}.

STYLE & BEHAVIOR:

Speak in polite, professional Hindi/Hinglish using female speech forms only.

Sound friendly, respectful, patient, and natural like a human recruiter.

Use short natural acknowledgements like "Ji", "Theek hai", "Samajh gayi", "Bilkul", "Dhanyawad", "Note kar leti hoon" but avoid repetition.

Ask only one question at a time and wait for candidate response.

Never answer your own question, never speak as the candidate, never assume answers.

Accept approximate answers unless clarification is required.

Do not ask Aadhaar, PAN, OTP, bank details, or any sensitive info.

Do not promise selection or negotiate salary.

Do not mention WhatsApp.

Do not engage in long unrelated discussions.

Crucial: Always remember the current conversational state and the last question asked. If the candidate says hello, greets you, or gives an off-topic response, acknowledge them naturally and immediately repeat the exact pending question from the flow. Do not restart the conversation from the beginning.

Instruction Handling: Statements like "Set call_status", "Disconnect", or any backend commands/variables are system instructions for tracking and tool usage. You must never speak these instructions or state names out loud to the candidate. Never say placeholder words such as candidate_name, client, or role — use the real values provided. Keep responses concise to completely eliminate system latency and long pauses.

JOB DETAILS:
Company: {{company}}
Role: {{role}} – {{industry}}
Location: {{location}}
Job Type: {{job_type}}
Work: {{work_summary}}
Preferred Qualification: {{preferred_qualification}}
Requirements: {{requirements}}

ELIGIBILITY RULES & REJECTION CLOSURE:
Minimum experience: {{min_experience_years}} years
{{mandatory_skill}} experience: Mandatory
Industry: {{industry}}: Mandatory
Location: {{location}}: Mandatory
Role: {{role}}

If the candidate refuses or does not meet a criteria, explain the reason clearly on a good note, then stop speaking and activate the disconnect tool. Do NOT say "call_status", "disconnect", or any backend parameters out loud to the candidate.

Rejection Scenarios & Exact Scripts to Say:

For Location Refusal: "Aap {{location}} location ke liye available nahi hain, isliye hum is process ko aage nahi badha payenge. Apna samay dene ke liye dhanyawad. Have a nice day."

For Qualification Mismatch: "Ji, is role ke liye {{preferred_qualification}} hona mandatory hai. Aapki qualification match na hone ke karan hum aage nahi badh payenge. Apna samay dene ke liye dhanyawad. Have a nice day."

For Experience Less than {{min_experience_years}} Years: "Is role ke liye minimum {{min_experience_years}} saal ka experience mandatory hai. Aapka experience kam hone ke karan hum aage nahi badh payenge. Apna samay dene ke liye dhanyawad. Have a nice day."

For Industry Mismatch: "Ji, is position ke liye {{industry}} ka background mandatory hai. Aapka industry background alag hone ke karan hum process aage nahi badha payenge. Apna samay dene ke liye dhanyawad. Have a nice day."

For No {{mandatory_skill}} Experience: "Is role ke liye {{mandatory_skill}} ka hands-on experience mandatory hai. Aapka isme exposure na hone ke karan hum aage nahi badh payenge. Apna samay dene ke liye dhanyawad. Have a nice day."

After speaking the appropriate script above:

Set call_status=Not Interested silently.

Immediately call disconnect_from_the_call.

QUESTION FLOW:
Note: Q1 (First Message) is handled externally in a different field. Start directly from Q2 when the candidate confirms interest.

Q2 – {{location}} Location Willingness:
"Kya aap {{location}} location ke liye available hain?"
Store as location_willingness. Wait for answer.
If No -> Say the Location Refusal script above, set call_status=Not Interested, disconnect.

Q3 – Qualification:
"Aapki highest qualification kya hai?"
Only accept: {{preferred_qualification}}
Store response as highest_qualification. Wait for answer.
If Mismatch -> Say the Qualification Mismatch script above, set call_status=Not Interested, disconnect.

Q4 – Experience & Industry:
"Aapko {{industry}} mein kitne saal ka work experience hai?"
Store as total_experience and current_industry. Wait for answer.
If less than {{min_experience_years}} years -> Say the Experience Less than {{min_experience_years}} Years script above, set call_status=Not Interested, disconnect.
If not from {{industry}} -> Say the Industry Mismatch script above, set call_status=Not Interested, disconnect.

Q5 – {{mandatory_skill}} Experience & Equipment Exposure:
"Kya aapko {{mandatory_skill}} ka experience hai, aur {{equipment_examples}} jaise equipments par aapne kitne saal kaam kiya hai?"
Store as utilities_experience and equipment_exposure_years. Wait for answer.
If no {{mandatory_skill}} experience -> Say the No {{mandatory_skill}} Experience script above, set call_status=Not Interested, disconnect.

Q6 – Final Confirmation:
"Aapki di hui jaankari ke hisaab se is role aur {{location}} location ke liye aapki ruchi bani hui hai na?"
Store as final_interest. Wait for answer.
If No -> Say the Location Refusal script above, set call_status=Not Interested, disconnect.

Closing after Q6 (Success):
Say: "Dhanyawad. Hamari recruitment team aapse aage ke hiring process ke liye contact karegi."
Set call_status=Completed silently.
Immediately call disconnect_from_the_call. Do not say anything else.

FAQ RESPONSES:
Salary/CTC: "Ji, salary ya CTC ki detailed jaankari recruitment team aage ke process mein share karegi."
Work: "Ji, is role mein {{work_summary}} ka kaam hoga."
Qualification: "Ji, {{preferred_qualification}} preferred hai."
Experience: "Ji, relevant experience wale candidates ko consider kiya ja raha hai."
Timing: "Ji, aam taur par timing 9 baje se 6 baje tak hoti hai, site requirement ke hisaab se thoda badal sakta hai."
Selection: "Ji, selection final process aur document verification ke baad confirm hoga."
Any Other Question: "Ji, iski detailed jaankari recruitment team aage ke process mein share karegi."

SILENT CAPTURE (Do not speak to candidate):
candidate_name
mobile_number
interested
location_willingness
highest_qualification
total_experience
current_industry
utilities_experience
equipment_exposure_years
final_interest
call_status
remarks`

export const DEFAULT_FIRST_MESSAGE_HINGLISH = `नमस्कार।
मेरा नाम {{agent_name}} है और मैं {{company}} से बात कर रही हूँ।
हम {{client}} के लिए {{location}} location पर {{role}} positions के लिए hiring कर रहे हैं।
क्या आप अभी 2 मिनट बात कर सकते हैं?`

export const DEFAULT_DRIVE_FIRST_MESSAGE_HINGLISH = `नमस्कार।
मेरा नाम {{agent_name}} है और मैं {{company}} से बात कर रही हूँ।
हम {{client}} के लिए {{drive_name}} recruitment drive के बारे में बात कर रहे हैं — {{drive_location}} पर, तारीखें: {{drive_dates}}।
{{role}} positions के लिए walk-in हो रहा है। क्या आप अभी 2 मिनट बात कर सकते हैं?`

export const DEFAULT_DRIVE_SYSTEM_PROMPT_HINGLISH = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. You are inviting candidates to a recruitment drive ({{drive_name}}) for {{client}} for the {{role}} role in {{industry}}, at {{drive_location}} on {{drive_dates}}.

STYLE & BEHAVIOR:

Speak in polite, professional Hindi/Hinglish using female speech forms only.

Sound friendly, respectful, patient, and natural like a human recruiter.

Use short natural acknowledgements like "Ji", "Theek hai", "Samajh gayi", "Bilkul", "Dhanyawad", "Note kar leti hoon" but avoid repetition.

Ask only one question at a time and wait for candidate response.

Never answer your own question, never speak as the candidate, never assume answers.

Do not ask Aadhaar, PAN, OTP, bank details, or any sensitive info.

Do not promise selection or negotiate salary.

Do not mention WhatsApp.

Instruction Handling: Statements like "Set call_status", "Disconnect", or any backend commands are system instructions. You must never speak these out loud to the candidate. Never say placeholder words such as candidate_name, client, or role — use the real values provided.

JOB DETAILS:
Company: {{company}}
Client: {{client}}
Drive: {{drive_name}}
Drive dates: {{drive_dates}}
Drive location: {{drive_location}}
Role: {{role}} – {{industry}}
Preferred Qualification: {{preferred_qualification}}
Requirements: {{requirements}}

QUESTION FLOW:
Note: Q1 (First Message) is handled externally. Start from Q2 when the candidate confirms interest.

Q2 – Drive interest:
"Kya aap {{drive_name}} drive mein {{drive_location}} par {{drive_dates}} ko participate karne mein interested hain?"
Store as drive_interest. Wait for answer.
If No -> politely thank and disconnect silently with call_status=Not Interested.

Q3 – Qualification:
"Aapki highest qualification kya hai?"
Only accept: {{preferred_qualification}}
Store as highest_qualification. Wait for answer.
If Mismatch -> explain qualification requirement, thank, disconnect with call_status=Not Interested.

Q4 – Location willingness:
"Kya aap {{drive_location}} location par drive ke liye aa sakte hain?"
Store as location_willingness. Wait for answer.
If No -> thank and disconnect with call_status=Not Interested.

Q5 – Final confirmation:
"Aapki di hui jaankari ke hisaab se aap drive mein interested hain na?"
Store as final_interest. Wait for answer.
If No -> thank and disconnect with call_status=Not Interested.

Closing after Q5 (Success):
Say: "Dhanyawad. Drive ki detailed jaankari aur venue details recruitment team aapko share karegi."
Set call_status=Completed silently.
Immediately call disconnect_from_the_call.

FAQ RESPONSES:
Salary/CTC: "Ji, salary ya CTC ki detailed jaankari drive ya recruitment team process mein share karegi."
Drive venue/timing: "Ji, exact venue aur timing recruitment team aapko confirm karegi."
Any Other Question: "Ji, iski detailed jaankari recruitment team aage ke process mein share karegi."

SILENT CAPTURE (Do not speak to candidate):
candidate_name
mobile_number
interested
drive_interest
highest_qualification
location_willingness
final_interest
call_status
remarks`

export const DEFAULT_FIRST_MESSAGE = `Hello.
My name is {{agent_name}} and I am calling from {{company}}.
We are hiring for {{role}} positions for {{client}} at {{location}}.
Do you have 2 minutes to talk right now?`

export const DEFAULT_DRIVE_FIRST_MESSAGE = `Hello.
My name is {{agent_name}} and I am calling from {{company}}.
I am calling about the {{drive_name}} hiring drive for {{client}} — at {{drive_location}} on {{drive_dates}}.
We have walk-in openings for {{role}}. Do you have 2 minutes to talk right now?`

export const DEFAULT_SYSTEM_PROMPT = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. You are screening candidates for the {{role}} role in the {{industry}} for {{client}}, based in {{location}}.

STYLE & BEHAVIOR:

Speak in polite, professional English. Sound friendly, respectful, patient, and natural like a human recruiter.

Use short natural acknowledgements like "Alright", "I understand", "Thank you", "Sure", "Let me note that" but avoid repetition.

Ask only one question at a time and wait for candidate response.

Never answer your own question, never speak as the candidate, never assume answers.

Accept approximate answers unless clarification is required.

Do not ask Aadhaar, PAN, OTP, bank details, or any sensitive info.

Do not promise selection or negotiate salary.

Do not mention WhatsApp.

Do not engage in long unrelated discussions.

Crucial: Always remember the current conversational state and the last question asked. If the candidate says hello, greets you, or gives an off-topic response, acknowledge them naturally and immediately repeat the exact pending question from the flow. Do not restart the conversation from the beginning.

Instruction Handling: Statements like "Set call_status", "Disconnect", or any backend commands/variables are system instructions for tracking and tool usage. You must never speak these instructions or state names out loud to the candidate. Never say placeholder words such as candidate_name, client, or role — use the real values provided. Keep responses concise to completely eliminate system latency and long pauses.

JOB DETAILS:
Company: {{company}}
Role: {{role}} – {{industry}}
Location: {{location}}
Job Type: {{job_type}}
Work: {{work_summary}}
Preferred Qualification: {{preferred_qualification}}
Requirements: {{requirements}}

ELIGIBILITY RULES & REJECTION CLOSURE:
Minimum experience: {{min_experience_years}} years
{{mandatory_skill}} experience: Mandatory
Industry: {{industry}}: Mandatory
Location: {{location}}: Mandatory
Role: {{role}}

If the candidate refuses or does not meet a criteria, explain the reason clearly on a good note, then stop speaking and activate the disconnect tool. Do NOT say "call_status", "disconnect", or any backend parameters out loud to the candidate.

Rejection Scenarios & Exact Scripts to Say:

For Location Refusal: "You are not available for the {{location}} location, so we will not be able to take this process further. Thank you for your time. Have a nice day."

For Qualification Mismatch: "This role requires {{preferred_qualification}}. Since your qualification does not match, we will not be able to move ahead. Thank you for your time. Have a nice day."

For Experience Less than {{min_experience_years}} Years: "This role needs a minimum of {{min_experience_years}} years of experience. Since your experience is less than that, we will not be able to move ahead. Thank you for your time. Have a nice day."

For Industry Mismatch: "This position requires a {{industry}} background. Since your industry background is different, we will not be able to take this process further. Thank you for your time. Have a nice day."

For No {{mandatory_skill}} Experience: "This role requires hands-on {{mandatory_skill}} experience. Since you do not have that exposure, we will not be able to move ahead. Thank you for your time. Have a nice day."

After speaking the appropriate script above:

Set call_status=Not Interested silently.

Immediately call disconnect_from_the_call.

QUESTION FLOW:
Note: Q1 (First Message) is handled externally in a different field. Start directly from Q2 when the candidate confirms interest.

Q2 – {{location}} Location Willingness:
"Are you available to work at the {{location}} location?"
Store as location_willingness. Wait for answer.
If No -> Say the Location Refusal script above, set call_status=Not Interested, disconnect.

Q3 – Qualification:
"What is your highest qualification?"
Only accept: {{preferred_qualification}}
Store response as highest_qualification. Wait for answer.
If Mismatch -> Say the Qualification Mismatch script above, set call_status=Not Interested, disconnect.

Q4 – Experience & Industry:
"How many years of work experience do you have in {{industry}}?"
Store as total_experience and current_industry. Wait for answer.
If less than {{min_experience_years}} years -> Say the Experience Less than {{min_experience_years}} Years script above, set call_status=Not Interested, disconnect.
If not from {{industry}} -> Say the Industry Mismatch script above, set call_status=Not Interested, disconnect.

Q5 – {{mandatory_skill}} Experience & Equipment Exposure:
"Do you have experience with {{mandatory_skill}}, and how many years have you worked with equipment like {{equipment_examples}}?"
Store as utilities_experience and equipment_exposure_years. Wait for answer.
If no {{mandatory_skill}} experience -> Say the No {{mandatory_skill}} Experience script above, set call_status=Not Interested, disconnect.

Q6 – Final Confirmation:
"Based on what you have shared, you are still interested in this role at the {{location}} location, correct?"
Store as final_interest. Wait for answer.
If No -> Say the Location Refusal script above, set call_status=Not Interested, disconnect.

Closing after Q6 (Success):
Say: "Thank you. Our recruitment team will contact you about the next steps in the hiring process."
Set call_status=Completed silently.
Immediately call disconnect_from_the_call. Do not say anything else.

FAQ RESPONSES:
Salary/CTC: "The recruitment team will share detailed salary or CTC information in the next steps."
Work: "In this role, the work involves {{work_summary}}."
Qualification: "{{preferred_qualification}} is preferred for this role."
Experience: "We are considering candidates with relevant experience."
Timing: "Timing is usually 9 to 6, and it can vary slightly based on site requirements."
Selection: "Selection is confirmed after the final process and document verification."
Any Other Question: "The recruitment team will share those details in the next steps."

SILENT CAPTURE (Do not speak to candidate):
candidate_name
mobile_number
interested
location_willingness
highest_qualification
total_experience
current_industry
utilities_experience
equipment_exposure_years
final_interest
call_status
remarks`

export const DEFAULT_DRIVE_SYSTEM_PROMPT = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. You are inviting candidates to a recruitment drive ({{drive_name}}) for {{client}} for the {{role}} role in {{industry}}, at {{drive_location}} on {{drive_dates}}.

STYLE & BEHAVIOR:

Speak in polite, professional English. Sound friendly, respectful, patient, and natural like a human recruiter.

Use short natural acknowledgements like "Alright", "I understand", "Thank you", "Sure", "Let me note that" but avoid repetition.

Ask only one question at a time and wait for candidate response.

Never answer your own question, never speak as the candidate, never assume answers.

Do not ask Aadhaar, PAN, OTP, bank details, or any sensitive info.

Do not promise selection or negotiate salary.

Do not mention WhatsApp.

Instruction Handling: Statements like "Set call_status", "Disconnect", or any backend commands are system instructions. You must never speak these out loud to the candidate. Never say placeholder words such as candidate_name, client, or role — use the real values provided.

JOB DETAILS:
Company: {{company}}
Client: {{client}}
Drive: {{drive_name}}
Drive dates: {{drive_dates}}
Drive location: {{drive_location}}
Role: {{role}} – {{industry}}
Preferred Qualification: {{preferred_qualification}}
Requirements: {{requirements}}

QUESTION FLOW:
Note: Q1 (First Message) is handled externally. Start from Q2 when the candidate confirms interest.

Q2 – Drive interest:
"Are you interested in attending the {{drive_name}} drive at {{drive_location}} on {{drive_dates}}?"
Store as drive_interest. Wait for answer.
If No -> politely thank and disconnect silently with call_status=Not Interested.

Q3 – Qualification:
"What is your highest qualification?"
Only accept: {{preferred_qualification}}
Store as highest_qualification. Wait for answer.
If Mismatch -> explain qualification requirement, thank, disconnect with call_status=Not Interested.

Q4 – Location willingness:
"Can you come to {{drive_location}} for the drive?"
Store as location_willingness. Wait for answer.
If No -> thank and disconnect with call_status=Not Interested.

Q5 – Experience:
"How many years of relevant experience do you have for the {{role}} role?"
Store as total_experience. Wait for answer.

Q6 – Final confirmation:
"Based on what you have shared, you are interested in attending this drive, correct?"
Store as final_interest. Wait for answer.
If No -> thank and disconnect with call_status=Not Interested.

Closing after Q6 (Success):
Say: "Thank you. Our recruitment team will share the drive details and venue with you."
Set call_status=Completed silently.
Immediately call disconnect_from_the_call.

FAQ RESPONSES:
Salary/CTC: "The recruitment team will share detailed salary or CTC information at the drive or in the next steps."
Drive venue/timing: "The recruitment team will confirm the exact venue and timing with you."
Any Other Question: "The recruitment team will share those details in the next steps."

SILENT CAPTURE (Do not speak to candidate):
candidate_name
mobile_number
interested
drive_interest
highest_qualification
total_experience
location_willingness
final_interest
call_status
remarks`

export const ENGLISH_SCREENING_AGENT_NAME = 'Screening EN'
export const ENGLISH_DRIVE_AGENT_NAME = 'Drive EN'
export const ENGLISH_SCREENING_AGENT_NAME_ALIASES = [
  'Screening EN',
  'Screening',
  'Screening agent (English)',
  'Screening (English)',
]
export const ENGLISH_DRIVE_AGENT_NAME_ALIASES = [
  'Drive EN',
  'Drive',
  'Drive agent (English)',
  'Drive (English)',
]
export const HINDI_DEFAULT_AGENT_ID = 'AGT_9A85BFD6'
/** Named English but Indus Config is Hi + Indus-hi-maya. Do not Test Call as English. */
export const BROKEN_HINDI_CONFIG_AGENT_IDS = ['AGT_2384D0F2']
export const BROKEN_ENGLISH_SCREENING_AGENT_ID = BROKEN_HINDI_CONFIG_AGENT_IDS[0]

export function isBrokenHindiConfigAgent(agentId) {
  const id = String(agentId || '').trim().toUpperCase()
  if (!id) return false
  return BROKEN_HINDI_CONFIG_AGENT_IDS.some(bad => String(bad).toUpperCase() === id)
}

/** Do not treat the org Hindi default as a newly created English agent. */
export function isForbiddenEnglishCreateReuseId(agentId) {
  const id = String(agentId || '').trim().toUpperCase()
  if (!id) return true
  if (id === String(HINDI_DEFAULT_AGENT_ID || '').toUpperCase()) return true
  return isBrokenHindiConfigAgent(id)
}

/** First usable English Screening/Drive id — skip Hindi-Config and the Hindi default. */
export function pickUsableEnglishAgentId(candidates = []) {
  const seen = new Set()
  for (let i = 0; i < candidates.length; i += 1) {
    const id = String(candidates[i] || '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    if (isBrokenHindiConfigAgent(id) || id === HINDI_DEFAULT_AGENT_ID) continue
    return id
  }
  return ''
}

/**
 * Official IndusLabs TTS voice_ids from https://docs.induslabs.io/tts
 * plus the production portal voice `Indus-multi-indu` (Indu · REALTIME · Multi).
 * IDs are `Indus-{lang}-{Name}` or `Indus-multi-{Name}`.
 */
export const INDUS_TTS_VOICES = [
  {
    id: 'Indus-multi-indu',
    language: 'multi',
    name: 'Indu',
    gender: 'female',
    provider: 'REALTIME',
    accent: 'Indian',
  },
  { id: 'Indus-en-maya', language: 'en', name: 'Maya', gender: 'female' },
  { id: 'Indus-en-Urvashi', language: 'en', name: 'Urvashi', gender: 'female' },
  { id: 'Indus-en-Ember', language: 'en', name: 'Ember', gender: 'female' },
  { id: 'Indus-hi-maya', language: 'hi', name: 'Maya', gender: 'female' },
  { id: 'Indus-hi-Urvashi', language: 'hi', name: 'Urvashi', gender: 'female' },
  { id: 'Indus-hi-Aditi', language: 'hi', name: 'Aditi', gender: 'female' },
  { id: 'Indus-hi-Arjun', language: 'hi', name: 'Arjun', gender: 'male' },
  { id: 'Indus-bn-Alivia', language: 'bn', name: 'Alivia', gender: 'female' },
  { id: 'Indus-bn-Sayan', language: 'bn', name: 'Sayan', gender: 'male' },
  { id: 'Indus-ar-Fatima', language: 'ar', name: 'Fatima', gender: 'female' },
  { id: 'Indus-ar-Hamdan', language: 'ar', name: 'Hamdan', gender: 'male' },
]

export const INDUS_MULTI_INDU_VOICE_ID = 'Indus-multi-indu'
/** Every agent defaults to Indu (Realtime · Multi). Language stays on transcript_language. */
export const DEFAULT_ENGLISH_VOICE_ID = INDUS_MULTI_INDU_VOICE_ID
export const DEFAULT_HINDI_VOICE_ID = INDUS_MULTI_INDU_VOICE_ID
export const DEFAULT_AGENT_VOICE_ID = INDUS_MULTI_INDU_VOICE_ID

/** Portal / legacy labels → official voice_id. "Indu" is Indus-multi-indu, not Maya. */
const INDUS_VOICE_ALIASES = {
  indu: INDUS_MULTI_INDU_VOICE_ID,
  'indus-multi-indu': INDUS_MULTI_INDU_VOICE_ID,
  'indus-en-default': INDUS_MULTI_INDU_VOICE_ID,
  'indus-hi-default': INDUS_MULTI_INDU_VOICE_ID,
  'maya (english)': 'Indus-en-maya',
  'maya (hindi)': 'Indus-hi-maya',
  'standard (english)': INDUS_MULTI_INDU_VOICE_ID,
  'standard (hindi)': INDUS_MULTI_INDU_VOICE_ID,
}

/** Indus click2call transcript_language codes we persist and send. */
export const TRANSCRIPT_LANGUAGE_OPTIONS = [
  { value: 'en', text: 'English' },
  { value: 'hi', text: 'Hindi' },
  { value: 'mr', text: 'Marathi' },
  { value: 'bn', text: 'Bengali' },
  { value: 'ar', text: 'Arabic' },
]

const LANGUAGE_ALIASES = {
  en: 'en',
  english: 'en',
  hi: 'hi',
  hindi: 'hi',
  hinglish: 'hi',
  mr: 'mr',
  marathi: 'mr',
  bn: 'bn',
  bengali: 'bn',
  bangla: 'bn',
  ar: 'ar',
  arabic: 'ar',
}

export function normalizeTranscriptLanguage(value) {
  const s = String(value || '').trim().toLowerCase().replace(/_/g, '-')
  if (!s) return ''
  if (LANGUAGE_ALIASES[s]) return LANGUAGE_ALIASES[s]
  const prefix = s.split('-')[0]
  if (LANGUAGE_ALIASES[prefix]) return LANGUAGE_ALIASES[prefix]
  return prefix.slice(0, 8)
}

export function parseIndusVoiceId(voice) {
  const raw = String(voice || '').trim()
  const match = raw.match(/^Indus-([A-Za-z]{2,8})-(.+)$/i)
  if (!match) return null
  return {
    language: normalizeTranscriptLanguage(match[1]) || match[1].toLowerCase(),
    name: match[2],
    id: raw,
  }
}

export function findOfficialIndusVoice(voice) {
  const key = String(voice || '').trim()
  if (!key) return null
  const catalog = getVoiceCatalog(INDUS_TTS_VOICES)
  return catalog.find(v => String(v.id || '').toLowerCase() === key.toLowerCase()) || null
}

export function canonicalizeIndusVoiceId(voice) {
  const raw = String(voice || '').trim()
  if (!raw) return ''
  const alias = INDUS_VOICE_ALIASES[raw.toLowerCase()]
  if (alias) return alias
  const official = findOfficialIndusVoice(raw)
  if (official) return official.id
  const parsed = parseIndusVoiceId(raw)
  return parsed ? `Indus-${parsed.language}-${parsed.name}` : raw
}

export function isMultilingualIndusVoice(voice) {
  const official = findOfficialIndusVoice(canonicalizeIndusVoiceId(voice))
  if (official && official.language === 'multi') return true
  const parsed = parseIndusVoiceId(canonicalizeIndusVoiceId(voice))
  return !!(parsed && parsed.language === 'multi')
}

export function languageFromIndusVoice(voice) {
  if (isMultilingualIndusVoice(voice)) return 'multi'
  const official = findOfficialIndusVoice(voice)
  if (official) return official.language
  const parsed = parseIndusVoiceId(canonicalizeIndusVoiceId(voice))
  return parsed ? parsed.language : ''
}

/** Catalog helper only — never auto-pick a voice at create/initiate. */
export function defaultVoiceForLanguage(/* lang */) {
  return ''
}

export function voicesForLanguage(lang) {
  const l = normalizeTranscriptLanguage(lang)
  const catalogLang = l === 'mr' ? 'hi' : l
  const catalog = getVoiceCatalog(INDUS_TTS_VOICES)
  const multi = catalog.filter(v => v.language === 'multi')
  const list = catalog.filter(v => v.language === catalogLang)
  const seen = new Set()
  return [...multi, ...list].filter((v) => {
    if (seen.has(v.id)) return false
    seen.add(v.id)
    return true
  })
}

export function voiceMatchesLanguage(voice, lang) {
  if (isMultilingualIndusVoice(voice)) return true
  const l = normalizeTranscriptLanguage(lang)
  const voiceLang = languageFromIndusVoice(voice)
  if (!voiceLang) return false
  if (l === 'mr') return voiceLang === 'mr' || voiceLang === 'hi'
  return voiceLang === l
}

export const AGENT_VOICE_OPTIONS = INDUS_TTS_VOICES.map(v => v.id)

export const AGENT_VOICE_LABELS = INDUS_TTS_VOICES.reduce((acc, v) => {
  acc[v.id] = v.language === 'multi'
    ? `${v.name} · ${v.id}`
    : `${v.name} · ${v.id}`
  return acc
}, {})

export function voiceOptionText(voice) {
  const official = findOfficialIndusVoice(canonicalizeIndusVoiceId(voice))
  if (official && official.language === 'multi') {
    return `${official.name} · ${official.id} (recommended · Realtime · Multi · Indian)`
  }
  return humanVoiceLabel(voice)
}

export function humanVoiceLabel(value) {
  const id = canonicalizeIndusVoiceId(value)
  if (AGENT_VOICE_LABELS[id]) return AGENT_VOICE_LABELS[id]
  const official = findOfficialIndusVoice(id)
  if (official) return `${official.name} · ${official.id}`
  return id || '—'
}

export function groupedVoiceSelectOptions() {
  const catalog = getVoiceCatalog(INDUS_TTS_VOICES)
  const multi = catalog.filter(v => v.language === 'multi')
  const groups = []
  if (multi.length) {
    groups.push({
      label: 'Realtime / Multi',
      options: multi.map(v => ({ value: v.id, text: voiceOptionText(v.id) })),
    })
  }
  getLanguageOptions(TRANSCRIPT_LANGUAGE_OPTIONS).forEach((lang) => {
    const voices = catalog.filter(v => v.language === (lang.value === 'mr' ? 'hi' : lang.value))
    if (!voices.length) return
    groups.push({
      label: lang.text,
      options: voices.map(v => ({ value: v.id, text: voiceOptionText(v.id) })),
    })
  })
  return groups
}

export function voiceSelectOptionsForLanguage(lang) {
  return voicesForLanguage(lang).map(v => ({
    value: v.id,
    text: voiceOptionText(v.id),
  }))
}

export function languageDisplayLabel(lang) {
  const n = normalizeTranscriptLanguage(lang)
  if (!n || n === 'multi') return n === 'multi' ? 'Multi (not a transcript language)' : ''
  const match = getLanguageOptions(TRANSCRIPT_LANGUAGE_OPTIONS).find(o => o.value === n)
  return match ? match.text : n
}

/** Stored pair only. Does not infer English/Hindi from the agent name. */
export function defaultAgentVoicePair() {
  return { language: '', voice: '' }
}

export function normalizeAgentVoiceEntry(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  let language = normalizeTranscriptLanguage(
    src.language || src.transcript_language || src.transcriptLanguage,
  )
  if (language === 'multi') language = ''
  const voice = canonicalizeIndusVoiceId(src.voice || src.voice_id || src.voiceId)
  return { language, voice }
}

export function normalizeAgentVoiceMap(raw, agents = []) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  const map = {}
  Object.keys(src).forEach((id) => {
    const key = String(id || '').trim()
    if (!key) return
    const agent = (agents || []).find(a => String(a.agent_id || a.id || '').trim() === key)
    map[key] = normalizeAgentVoiceEntry(src[key], {
      agentId: key,
      agentName: agent && (agent.name || agent.agent_name || agent.title),
    })
  })
  return map
}

export function getAgentVoiceSettings(map, agentId) {
  const id = String(agentId || '').trim()
  const stored = map && id && map[id]
  if (stored) return normalizeAgentVoiceEntry(stored)
  return defaultAgentVoicePair()
}

/** True when Admin saved a language/voice row for this AGT_ id. */
export function hasStoredAgentVoice(map, agentId) {
  const id = String(agentId || '').trim()
  if (!id || !map || typeof map !== 'object' || Array.isArray(map)) return false
  const raw = map[id]
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false
  return !!(
    raw.language
    || raw.transcript_language
    || raw.transcriptLanguage
    || raw.voice
    || raw.voice_id
    || raw.voiceId
  )
}

export function formatAgentVoiceLine(settings) {
  const s = settings && typeof settings === 'object' ? settings : {}
  return `${humanVoiceLabel(s.voice)} · ${languageDisplayLabel(s.language)}`
}

export function isMarathiVoiceName(voice) {
  const v = String(voice || '').trim().toLowerCase()
  return v.startsWith('indus-mr') || v.includes('marathi')
}

export function looksLikeEnglishAgent({ agentId, agentName } = {}) {
  const id = String(agentId || '').trim()
  if (id && id === HINDI_DEFAULT_AGENT_ID) return false
  const name = String(agentName || '').toLowerCase()
  if (name.includes('english') || /\(en\)/.test(name)) return true
  return false
}

/**
 * Language for POST /api/ai-calls/initiate (`transcript_language` = post-call STT).
 * Priority: Admin per-agent map → explicit form language → org config language.
 * No `en`, no preferEnglish, no agent-name heuristic, no Multi.
 */
export function resolveInitiateTranscriptLanguage(input = {}) {
  const map = input.agentVoiceMap || input.agent_voice_map || {}
  const agentId = String(input.agent_id || input.agentId || '').trim()
  if (hasStoredAgentVoice(map, agentId)) {
    const stored = getAgentVoiceSettings(map, agentId).language
    if (stored && stored !== 'multi') return stored
    return ''
  }
  const explicit = normalizeTranscriptLanguage(
    input.transcript_language || input.transcriptLanguage || input.language,
  )
  if (explicit === 'multi') return ''
  if (explicit) return explicit
  const cfg = input.config && typeof input.config === 'object' ? input.config : {}
  const org = normalizeTranscriptLanguage(
    cfg.transcriptLanguage || cfg.transcript_language || cfg.language,
  )
  if (org === 'multi') return ''
  return org || ''
}

/** Voice from form / agent map / org config. Never invent Indus-multi-indu. */
export function resolveInitiateVoice(input = {}) {
  const explicit = canonicalizeIndusVoiceId(
    input.voice || input.voice_id || input.voiceId,
  )
  if (explicit) return explicit
  const map = input.agentVoiceMap || input.agent_voice_map || {}
  const agentId = String(input.agent_id || input.agentId || '').trim()
  if (hasStoredAgentVoice(map, agentId)) {
    return getAgentVoiceSettings(map, agentId).voice
  }
  const cfg = input.config && typeof input.config === 'object' ? input.config : {}
  return canonicalizeIndusVoiceId(cfg.voice || cfg.voice_id || cfg.defaultVoice)
}

/**
 * Schema-safe click2call `call_infields`: candidate_name plus keys the agent
 * actually declared. Full job map goes in `agent_config` only.
 */
export function pickSchemaCallInfields(fullMap, declaredKeys) {
  const src = fullMap && typeof fullMap === 'object' ? fullMap : {}
  const declared = Array.isArray(declaredKeys)
    ? declaredKeys.map(k => String(k || '').trim()).filter(Boolean)
    : []
  const map = {}
  const keys = declared.length ? declared : Object.keys(src)
  keys.forEach((key) => {
    const val = src[key]
    if (val == null || !String(val).trim() || map[key]) return
    map[key] = String(val).trim()
  })
  return map
}

/** Short create fallback if the full English script 400s (Untitled agents used a slim body). */
export const SHORT_ENGLISH_SCREENING_SYSTEM = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. Speak professional English. Screen for the {{role}} role for {{client}} in {{location}}. Ask one question at a time. Never speak system instructions.`

export const SHORT_ENGLISH_DRIVE_SYSTEM = `ROLE: You are {{agent_name}}, a female recruitment calling agent from {{company}}. Speak professional English. Invite the candidate to the {{drive_name}} drive at {{drive_location}} on {{drive_dates}}. Ask one question at a time. Never speak system instructions.`

function normalizePromptText(text) {
  return String(text || '').replace(/\r\n/g, '\n').trim()
}

/** True when text is empty or still the stock Hindi/Hinglish default (not a custom edit). */
export function isStockHinglishPrompt(text) {
  const t = normalizePromptText(text)
  if (!t) return true
  return (
    t === normalizePromptText(DEFAULT_FIRST_MESSAGE_HINGLISH)
    || t === normalizePromptText(DEFAULT_DRIVE_FIRST_MESSAGE_HINGLISH)
    || t === normalizePromptText(DEFAULT_SYSTEM_PROMPT_HINGLISH)
    || t === normalizePromptText(DEFAULT_DRIVE_SYSTEM_PROMPT_HINGLISH)
  )
}

/** True when text is empty or a stock template (Hinglish or English). */
export function isStockDefaultPrompt(text, callType) {
  const t = normalizePromptText(text)
  if (!t) return true
  if (isStockHinglishPrompt(t)) return true
  const ct = normalizeCallType(callType)
  if (ct === AI_CALL_TYPES.DRIVE) {
    return t === normalizePromptText(DEFAULT_DRIVE_FIRST_MESSAGE)
      || t === normalizePromptText(DEFAULT_DRIVE_SYSTEM_PROMPT)
  }
  return t === normalizePromptText(DEFAULT_FIRST_MESSAGE)
    || t === normalizePromptText(DEFAULT_SYSTEM_PROMPT)
}

/** Resolve default first message for call type (English). */
export function getDefaultFirstMessage(callType) {
  const t = normalizeCallType(callType)
  if (t === AI_CALL_TYPES.DRIVE) return DEFAULT_DRIVE_FIRST_MESSAGE
  return DEFAULT_FIRST_MESSAGE
}

/** Resolve default system prompt for call type (English). */
export function getDefaultSystemPrompt(callType) {
  const t = normalizeCallType(callType)
  if (t === AI_CALL_TYPES.DRIVE) return DEFAULT_DRIVE_SYSTEM_PROMPT
  return DEFAULT_SYSTEM_PROMPT
}

export function getEnglishOrgAgentName(callType) {
  return normalizeCallType(callType) === AI_CALL_TYPES.DRIVE
    ? ENGLISH_DRIVE_AGENT_NAME
    : ENGLISH_SCREENING_AGENT_NAME
}

/** Spoken demo name when a test call has no matched candidate. */
export const TEST_CALL_DEMO_CANDIDATE_NAME = 'Rahul Sharma'

/** Matches {{var}} first, then {var}, so double-brace tokens are not split. */
const DOUBLE_OR_SINGLE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}|\{([a-zA-Z0-9_]+)\}/g

function replacePromptTokens(template, resolver) {
  return String(template || '').replace(DOUBLE_OR_SINGLE_TOKEN_RE, (full, doubleKey, singleKey) => {
    const key = doubleKey || singleKey
    return resolver(key, full)
  })
}

function cleanPromptValue(val) {
  if (val == null) return ''
  return String(val).trim()
}

/** Substitute {{var}} / {var} tokens using sample (or provided) values. */
export function fillPromptTemplate(template, values = {}) {
  const map = {}
  AI_CALLING_VARIABLES.forEach(v => {
    const provided = cleanPromptValue(values[v.key])
    map[v.key] = provided || v.sample
  })
  Object.keys(values || {}).forEach(k => {
    const provided = cleanPromptValue(values[k])
    if (provided) map[k] = provided
  })
  return replacePromptTokens(template, (key, full) => (
    Object.prototype.hasOwnProperty.call(map, key) ? map[key] : full
  ))
}

/**
 * Substitute only keys present in `values`.
 * Leaves other {{tokens}} / {tokens} unchanged (e.g. {{candidate_name}} for runtime).
 * Empty / null values do not replace the token.
 */
export function applyPromptVariables(template, values = {}) {
  const map = values || {}
  return replacePromptTokens(template, (key, full) => {
    if (!Object.prototype.hasOwnProperty.call(map, key)) return full
    const val = cleanPromptValue(map[key])
    if (!val) return full
    return val
  })
}

/** True if text still has {{var}} or {var} tokens TTS could speak. */
export function spokenTextHasTokens(text) {
  return /\{\{\s*[a-zA-Z0-9_]+\s*\}\}|\{[a-zA-Z0-9_]+\}/.test(String(text || ''))
}

/** Keys the script / Job details form bind as {{token}} → infields.token. */
export const INFIELD_SCRIPT_KEYS = AI_CALLING_VARIABLES.map(v => v.key)

/**
 * Concrete infields map for Indus (same keys as {{tokens}}).
 * Always includes Job-details chips; never leaves agent_name as an AGT_ id.
 */
export function normalizeCallInfieldsMap(values = {}) {
  const src = values && typeof values === 'object' ? values : {}
  const map = {}
  INFIELD_SCRIPT_KEYS.forEach((key) => {
    const val = formatNamedField(src[key])
    if (val && !(key === 'agent_name' && looksLikeAgentCode(val))) {
      map[key] = val
    }
  })
  Object.keys(src).forEach((key) => {
    if (map[key]) return
    const val = formatNamedField(src[key])
    if (!val) return
    if (key === 'agent_name' && looksLikeAgentCode(val)) return
    map[key] = val
  })
  if (!map.agent_name || looksLikeAgentCode(map.agent_name)) {
    map.agent_name = DEFAULT_AGENT_NAME
  }
  if (!map.company) map.company = DEFAULT_COMPANY_NAME
  if (map.company && !map.company_name) map.company_name = map.company
  if (map.client && !map.client_name) map.client_name = map.client
  if (map.client_name && !map.client) map.client = map.client_name
  if (map.role && !map.job_title) map.job_title = map.role
  if (map.job_title && !map.position_name) map.position_name = map.job_title
  if (map.role && !map.position_name) map.position_name = map.role
  if (map.preferred_qualification && !map.qualification) {
    map.qualification = map.preferred_qualification
  }
  if (map.min_experience_years && !map.min_exp) map.min_exp = map.min_experience_years
  if (map.max_experience_years && !map.max_exp) map.max_exp = map.max_experience_years
  if (!map.experience) {
    map.experience = formatExperienceRange(
      map.min_experience_years || map.min_exp,
      map.max_experience_years || map.max_exp,
    )
  }
  if ((map.min_ctc || map.max_ctc) && !map.ctc) {
    map.ctc = formatCtcRange(map.min_ctc, map.max_ctc)
  }
  if (map.mandatory_skill && !map.equipment_examples) {
    map.equipment_examples = map.mandatory_skill
  }
  INFIELD_SCRIPT_KEYS.forEach((key) => {
    if (!map[key] && SPOKEN_FALLBACKS[key]) map[key] = SPOKEN_FALLBACKS[key]
  })
  return map
}

/**
 * Job-screening infields sent on initiate (flat strings).
 * Indus should declare these TEXT VISIBLE. 502 with only candidate_name
 * already happened — extra keys are not stripped to "fix" 502.
 */
export const INITIATE_INFIELD_KEYS = [
  'candidate_name',
  'client_name',
  'client',
  'company',
  'company_name',
  'role',
  'job_title',
  'position_name',
  'location',
  'industry',
  'min_experience_years',
  'experience',
  'min_exp',
  'max_experience_years',
  'max_exp',
  'min_ctc',
  'max_ctc',
  'ctc',
  'qualification',
  'preferred_qualification',
  'agent_name',
]

/** Drive extras — only when call type is drive. */
export const DRIVE_INITIATE_INFIELD_KEYS = [
  'drive_name',
  'drive_dates',
  'drive_location',
]

export const DEFAULT_DECLARED_INFIELD_KEYS = [...INITIATE_INFIELD_KEYS]

const INFIELD_SCHEMA_SKIP = new Set([
  'system_prompt',
  'starting_instructions',
  'firstMessage',
  'first_message',
  'systemPrompt',
  'infields',
  'call_infields',
  'call_infield',
])

/**
 * Names from an Indus/ATS agent record's call_infield schema.
 * Does not treat a values map as schema (that would re-send undeclared keys).
 */
export function extractDeclaredInfieldKeys(source) {
  const names = new Set()
  const addName = (name) => {
    const key = String(name || '').trim()
    if (!key || INFIELD_SCHEMA_SKIP.has(key)) return
    names.add(key)
  }
  const walkList = (list) => {
    if (Array.isArray(list)) {
      list.forEach((item) => {
        if (typeof item === 'string') addName(item)
        else if (item && typeof item === 'object') {
          addName(item.field_name || item.name || item.key || item.field)
        }
      })
      return
    }
    if (list && typeof list === 'object') {
      addName(list.field_name || list.name || list.key || list.field)
    }
  }
  const src = source && typeof source === 'object' ? source : {}
  const nested = src.data && typeof src.data === 'object' && !Array.isArray(src.data)
    ? src.data
    : {}
  const cfg = src.agent_config && typeof src.agent_config === 'object' ? src.agent_config : {}
  const nestedCfg = nested.agent_config && typeof nested.agent_config === 'object'
    ? nested.agent_config
    : {}
  walkList(src.call_infield)
  walkList(src.declared_infields)
  walkList(src.declaredInfields)
  walkList(nested.call_infield)
  walkList(nested.declared_infields)
  walkList(cfg.call_infield)
  walkList(nestedCfg.call_infield)
  walkList(Array.isArray(src.call_infields) ? src.call_infields : null)
  walkList(Array.isArray(nested.call_infields) ? nested.call_infields : null)
  if (!names.size) return []
  return [...names]
}

function configuredAliasMap(opts = {}) {
  const raw = opts.infieldAliases || opts.infield_aliases
    || (opts.config && (opts.config.infieldAliases || opts.config.infield_aliases))
    || {}
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  return raw
}

function valueForInfieldKey(src, key, aliases) {
  const direct = formatNamedField(src[key])
  if (direct) return direct
  const list = Array.isArray(aliases[key])
    ? aliases[key]
    : (aliases[key] ? [aliases[key]] : [])
  for (let i = 0; i < list.length; i += 1) {
    const alias = String(list[i] || '').trim()
    if (!alias || alias === key) continue
    const val = formatNamedField(src[alias])
    if (val) return val
  }
  return ''
}

/**
 * Initiate infields — configured/declared keys with real values only.
 * No candidate_name / company / client alias invention unless config aliases exist.
 */
export function slimInitiateInfields(values = {}, declaredKeys = [], opts = {}) {
  const src = values && typeof values === 'object' ? values : {}
  const extra = Array.isArray(declaredKeys)
    ? declaredKeys.map(k => String(k || '').trim()).filter(Boolean)
    : []
  const configured = Array.isArray(opts.configuredKeys)
    ? opts.configuredKeys.map(k => String(k || '').trim()).filter(Boolean)
    : []
  const keep = []
  const seen = new Set()
  const callType = normalizeCallType(opts.callType || opts.call_type || src.call_type)
  const catalogKeys = getInitiateInfieldKeys(INITIATE_INFIELD_KEYS).concat(
    callType === AI_CALL_TYPES.DRIVE
      ? getDriveInitiateInfieldKeys(DRIVE_INITIATE_INFIELD_KEYS)
      : [],
  )
  const keys = extra.length
    ? extra
    : (configured.length ? configured : (catalogKeys.length ? catalogKeys : Object.keys(src)))
  keys.forEach((key) => {
    const name = String(key || '').trim()
    if (!name || seen.has(name) || INFIELD_SCHEMA_SKIP.has(name)) return
    seen.add(name)
    keep.push(name)
  })
  const aliases = configuredAliasMap(opts)
  const map = {}
  keep.forEach((key) => {
    const val = valueForInfieldKey(src, key, aliases)
    if (!val) return
    if (key === 'agent_name' && looksLikeAgentCode(val)) return
    map[key] = val
  })
  return map
}

/** Max Indus Config slots (Call Infields / Call Outcomes 0/15). */
export const INDUS_SCHEMA_SLOT_LIMIT = 15

/** Screening create infields — official TEXT VISIBLE names, ≤15. */
export const SCREENING_CREATE_INFIELD_KEYS = [
  'candidate_name',
  'client_name',
  'company',
  'role',
  'location',
  'industry',
  'min_ctc',
  'max_ctc',
  'min_exp',
  'experience',
  'qualification',
  'agent_name',
  'job_title',
  'max_exp',
  'preferred_qualification',
]

/** Drive create infields — job keys + drive extras, ≤15. */
export const DRIVE_CREATE_INFIELD_KEYS = [
  'candidate_name',
  'client_name',
  'company',
  'role',
  'location',
  'industry',
  'min_ctc',
  'max_ctc',
  'min_exp',
  'experience',
  'qualification',
  'agent_name',
  'drive_name',
  'drive_dates',
  'drive_location',
]

/** Official Indus create/POST call_infield objects (docs: field_name, field_type, is_visible). */
export function buildOfficialCallInfield(fieldName, fieldType = 'TEXT') {
  const name = String(fieldName || '').trim()
  if (!name) return null
  return {
    field_name: name,
    field_type: String(fieldType || 'TEXT').toUpperCase() || 'TEXT',
    field_enum: null,
    is_visible: true,
  }
}

export function createInfieldKeysForCallType(callType) {
  const ct = normalizeCallType(callType)
  const fields = getCreateInfieldsForCallType(ct, {})
  return fields.map(f => f.key)
}

/**
 * Official Indus create schema array.
 * Docs example is one object; playground has 15 slots — send an array of
 * { field_name, field_type: TEXT, is_visible }.
 * Keys/types come from admin catalog when configured.
 */
export function buildOfficialCallInfieldSchema(callType, extraKeys = []) {
  const seen = new Set()
  const schema = []
  const ct = normalizeCallType(callType)
  const configured = getCreateInfieldsForCallType(ct, {})
  const extras = extraKeys || []
  const source = configured.length ? configured : extras.map(key => ({ key, type: 'TEXT', visible: true }))
  source.forEach((field) => {
    const item = typeof field === 'string'
      ? buildOfficialCallInfield(field)
      : buildOfficialCallInfield(field.key, field.type)
    if (!item || seen.has(item.field_name)) return
    item.is_visible = field.visible !== false
    seen.add(item.field_name)
    schema.push(item)
  })
  return schema
}

/** Legacy alias — official field names (not { name, type, visible }). */
export function buildCallInfieldSchema(map = {}, callType) {
  return buildOfficialCallInfieldSchema(callType, Object.keys(map || {}))
}

export const SCREENING_CREATE_OUTCOMES = [
  {
    outcome_name: 'call_status',
    outcome_description: 'Call result',
    outcome_type: 'FIXED',
    outcome_enum: ['Completed', 'Not Interested', 'Call Back', 'Not Reachable'],
    is_visible: true,
  },
  {
    outcome_name: 'interested',
    outcome_description: 'Candidate interest',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'final_interest',
    outcome_description: 'Final interest confirmation',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'highest_qualification',
    outcome_description: 'Highest qualification',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'total_experience',
    outcome_description: 'Years of experience',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'current_industry',
    outcome_description: 'Current industry',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'location_willingness',
    outcome_description: 'Willing to work at the job location',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'remarks',
    outcome_description: 'Recruiter notes',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
]

export const DRIVE_CREATE_OUTCOMES = [
  {
    outcome_name: 'call_status',
    outcome_description: 'Call result',
    outcome_type: 'FIXED',
    outcome_enum: ['Completed', 'Not Interested', 'Call Back', 'Not Reachable'],
    is_visible: true,
  },
  {
    outcome_name: 'interested',
    outcome_description: 'Candidate interest',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'drive_interest',
    outcome_description: 'Interest in attending the drive',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'highest_qualification',
    outcome_description: 'Highest qualification',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'total_experience',
    outcome_description: 'Years of experience',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'location_willingness',
    outcome_description: 'Can attend the drive location',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'final_interest',
    outcome_description: 'Final drive confirmation',
    outcome_type: 'BOOLEAN',
    outcome_enum: null,
    is_visible: true,
  },
  {
    outcome_name: 'remarks',
    outcome_description: 'Recruiter notes',
    outcome_type: 'TEXT',
    outcome_enum: null,
    is_visible: true,
  },
]

export function buildOfficialCallOutcomeSchema(callType) {
  const ct = normalizeCallType(callType)
  const list = getCreateOutcomesForCallType(ct, {})
  return list.map(item => ({
    outcome_name: item.outcome_name,
    outcome_description: item.outcome_description,
    outcome_type: item.outcome_type,
    outcome_enum: item.outcome_enum,
    is_visible: item.is_visible !== false,
  }))
}

/** Unique {{var}} / {var} names in template text. */
export function extractPromptTokens(template) {
  const found = []
  const seen = new Set()
  replacePromptTokens(template, (key, full) => {
    if (key && !seen.has(key)) {
      seen.add(key)
      found.push(key)
    }
    return full
  })
  return found
}

/**
 * Resolve templates for a live/test call so TTS never speaks placeholder keys.
 * @param {string} template
 * @param {Object} values
 * @param {{ keepTokens?: string[], useSamples?: boolean, stripUnknown?: boolean }} [opts]
 */
export function resolvePromptForCall(template, values = {}, opts = {}) {
  const keep = new Set(opts.keepTokens || [])
  const merged = { ...(values || {}) }
  if (!cleanPromptValue(merged.candidate_name) && !keep.has('candidate_name')) {
    merged.candidate_name = TEST_CALL_DEMO_CANDIDATE_NAME
  }
  if (!cleanPromptValue(merged.agent_name) || looksLikeAgentCode(merged.agent_name)) {
    merged.agent_name = DEFAULT_AGENT_NAME
  }
  const usable = {}
  Object.keys(merged).forEach(k => {
    if (keep.has(k)) return
    const val = cleanPromptValue(merged[k])
    if (val) usable[k] = val
  })
  extractPromptTokens(template).forEach(key => {
    if (keep.has(key) || usable[key]) return
    if (SPOKEN_FALLBACKS[key]) usable[key] = SPOKEN_FALLBACKS[key]
  })
  let out = applyPromptVariables(template, usable)
  if (opts.useSamples !== false) {
    out = applyPromptVariables(out, SPOKEN_FALLBACKS)
  }
  if (opts.stripUnknown !== false) {
    out = replacePromptTokens(out, (key, full) => (keep.has(key) ? full : ''))
  }
  return out
}
